import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownRight, Clock, CheckCircle, XCircle, QrCode, X, CreditCard, ExternalLink, RefreshCw } from 'lucide-react';

const RAZORPAY_PAYMENT_URL = 'https://razorpay.me/@cloudsetuprivatelimited';
import WalletTopup from '@/components/WalletTopup';
import { CouponRedeemSection } from '@/components/CouponRedeemSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UPI QR Code - CloudSetu Pvt Ltd
const UPI_QR_CODE = '/upi-qrcode.png';
const UPI_ID = 'yespay.smessi10139493@yesbankltd';
const COMPANY_NAME = 'CLOUDSETU PRIVATE LIMITED';

export default function WalletPage() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addMoneyOpen, setAddMoneyOpen] = useState(false);
  const [topupOpen, setTopupOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [upiSubmitSuccess, setUpiSubmitSuccess] = useState(false);
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    utr_number: '',
    payment_date: new Date().toISOString().split('T')[0]
  });

  // Coupon state removed — handled by shared CouponRedeemSection component

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [walletRes, transactionsRes, requestsRes] = await Promise.all([
        axios.get(`${API}/wallet/balance`),
        axios.get(`${API}/wallet/transactions`),
        axios.get(`${API}/wallet/payment-requests`)
      ]);
      setWallet(walletRes.data);
      setTransactions(transactionsRes.data);
      setPaymentRequests(requestsRes.data);
    } catch (error) {
      toast.error('Failed to fetch wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleTopupSuccess = (result) => {
    fetchData();
    // Refresh DashboardLayout navbar balance
    window.dispatchEvent(new CustomEvent('cloudsetu:wallet-updated')); // Refresh wallet data
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.amount || !paymentForm.utr_number) {
      toast.error('Please fill in amount and UTR number');
      return;
    }

    if (parseFloat(paymentForm.amount) <= 0) {
      toast.error('Amount must be positive');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/wallet/payment-request`, {
        amount: parseFloat(paymentForm.amount),
        utr_number: paymentForm.utr_number,
        payment_date: paymentForm.payment_date,
        notes: ''
      });
      setUpiSubmitSuccess(true);
      setPaymentForm({
        amount: '',
        utr_number: '',
        payment_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  return (
    <DashboardLayout role={user?.role}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-start mb-3 flex-wrap md:nowrap">
          <div className="mb-2 mr-0 sm:mr-4">
            <h1 className="text-3xl font-bold text-slate-800" data-testid="wallet-title">Wallet</h1>
            <p className="text-slate-500 mt-1">Manage your wallet balance and transactions</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="p-2 rounded-lg hover:bg-slate-100 border border-slate-200" title="Refresh" data-testid="refresh-btn"><RefreshCw className="h-4 w-4 text-slate-500" /></button>
            <Button 
              onClick={() => setTopupOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="razorpay-topup-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Pay Online
            </Button>
            <Dialog open={addMoneyOpen} onOpenChange={(open) => { setAddMoneyOpen(open); if (!open) setUpiSubmitSuccess(false); }}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-money-btn">
                  <QrCode className="h-4 w-4 mr-2" />
                  Pay via UPI
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl p-0 overflow-hidden h-[90vh] md:h-auto overflow-y-auto" data-testid="add-money-dialog">
                {/* Dialog Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
                    <QrCode className="h-5 w-5" />
                    Add Money via UPI
                  </DialogTitle>
                  
                </div>

              {/* Success State */}
              {upiSubmitSuccess ? (
                <div className="p-10 flex flex-col items-center justify-center text-center" data-testid="upi-success-state">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">Payment Request Submitted!</h3>
                  <p className="text-slate-600 max-w-sm mb-1">
                    Your payment is being verified and will be automatically added to your wallet once confirmed.
                  </p>
                  <p className="text-sm text-slate-500 max-w-sm mb-6">
                    You can track the status in the <span className="font-medium text-blue-600">Payment Requests</span> section below.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-sm w-full text-sm text-blue-800 mb-6">
                    <strong>What happens next?</strong>
                    <ul className="mt-2 space-y-1 text-left list-disc list-inside">
                      <li>Admin reviews your UTR number</li>
                      <li>Payment is verified against bank records</li>
                      <li>Amount is credited to your wallet automatically</li>
                    </ul>
                  </div>
                  <Button onClick={() => { setAddMoneyOpen(false); setUpiSubmitSuccess(false); }} className="bg-blue-600 hover:bg-blue-700 text-white px-8">
                    Done
                  </Button>
                </div>
              ) : (
              <div className="flex flex-col md:flex-row">
                {/* Left Side - QR Code */}
                <div className="flex-1 p-6 flex flex-col items-center justify-center border-r border-slate-100 bg-slate-50">
                  {/* Bank Logo */}
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center">
                      <span className="text-white font-bold text-xs">Y</span>
                    </div>
                    <span className="text-blue-600 font-semibold text-sm">YES Bank</span>
                  </div>
                  
                  {/* Pay To */}
                  <p className="text-xs text-slate-500 mb-1">Pay to</p>
                  <p className="font-bold text-slate-800 mb-4">{COMPANY_NAME}</p>
                  
                  {/* QR Code - Larger Size for better scanning */}
                  <div className="bg-white p-4 rounded-lg shadow-sm mb-4">
                    <img 
                      src={UPI_QR_CODE} 
                      alt={`UPI QR Code - Pay to ${COMPANY_NAME}`} 
                      className="w-72 h-72 object-contain"
                      data-testid="upi-qr-code"
                    />
                  </div>
                  
                  {/* UPI ID */}
                  <p className="text-sm text-slate-600">UPI ID: <span className="font-medium text-slate-800">{UPI_ID}</span></p>

                  {/* Divider */}
                  <div className="flex items-center gap-2 w-full my-3">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400 font-medium">or</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>

                  {/* Pay via Razorpay */}
                  <a
                    href={RAZORPAY_PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-[#072654] hover:bg-[#0a347a] text-white text-sm font-semibold transition-colors"
                    data-testid="pay-via-razorpay-btn"
                  >
                    <CreditCard className="h-4 w-4" />
                    Pay via Razorpay
                    <ExternalLink className="h-3.5 w-3.5 opacity-70" />
                  </a>
                </div>
                
                {/* Right Side - Form */}
                <div className="flex-1 p-6">
                  <div className="space-y-5">
                    {/* Amount Field */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">
                        Amount Paid (₹) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        placeholder="Enter amount"
                        value={paymentForm.amount}
                        onChange={(e) => setPaymentForm(prev => ({...prev, amount: e.target.value}))}
                        className="h-11"
                        data-testid="payment-amount"
                      />
                    </div>
                    
                    {/* UTR Field */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">
                        UTR / Transaction Reference <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter 12-digit UTR number"
                        value={paymentForm.utr_number}
                        onChange={(e) => setPaymentForm(prev => ({...prev, utr_number: e.target.value}))}
                        className="h-11"
                        data-testid="payment-utr"
                      />
                      <p className="text-xs text-slate-500">Find this in file in your UPI PI appen payment history</p>
                    </div>
                    
                    {/* Payment Date Field */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-slate-700">Payment Date</Label>
                      <Input
                        type="date"
                        value={paymentForm.payment_date}
                        onChange={(e) => setPaymentForm(prev => ({...prev, payment_date: e.target.value}))}
                        className="h-11"
                        data-testid="payment-date"
                      />
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex justify-end gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => setAddMoneyOpen(false)}
                        className="px-6"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleSubmitPayment} 
                        disabled={submitting}
                        className="bg-blue-600 hover:bg-blue-700 px-6"
                        data-testid="submit-payment-btn"
                      >
                        {submitting ? 'Submitting...' : 'Submit Payment'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Balance Card */}
            <Card className="bg-white border-0 shadow-sm" data-testid="balance-card">
              <CardContent className="p-6 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-slate-500 text-sm">Available Balance</p>
                    <p className="text-4xl font-bold text-slate-800 mt-1">₹{wallet?.balance?.toLocaleString() || '0'}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl">
                    <WalletIcon className="h-10 w-10 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Requests */}
            {paymentRequests.length > 0 && (
              <Card className="bg-white border-0 shadow-sm mb-4" data-testid="payment-requests-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold text-slate-800">Payment Requests</CardTitle>
                  <CardDescription>Your submitted payment requests</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {paymentRequests.map(request => (
                      <div 
                        key={request.id} 
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-2"
                        data-testid={`payment-request-${request.id}`}
                      >
                        <div className="space-y-1">
                          <p className="font-medium text-slate-800">₹{(request.amount || 0).toLocaleString()}</p>
                          <p className="text-sm text-slate-500">UTR: {request.utr_number}</p>
                          <p className="text-xs text-slate-400">{new Date(request.created_at).toLocaleString()}</p>
                        </div>
                        <div className="text-right space-y-2">
                          {getStatusBadge(request.status)}
                          {request.admin_notes && (
                            <p className="text-xs text-slate-500 max-w-[200px]">{request.admin_notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Coupon Redeem Section — available for all partners */}
            <CouponRedeemSection
              className="mb-4"
              onRedeemed={() => fetchData()}
              data-testid="wallet-coupon-section"
            />

            {/* Transaction History */}
            <Card className="bg-white border-0 shadow-sm" data-testid="transactions-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold text-slate-800">Transaction History</CardTitle>
                <CardDescription>Your wallet transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <WalletIcon className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3 overflow-x-auto">
                    {transactions.map(txn => (
                      <div 
                        key={txn.id} 
                        className="flex items-center justify-between p-4 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors mb-2"
                        data-testid={`transaction-${txn.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${txn.type === 'credit' ? 'bg-green-50' : 'bg-red-50'}`}>
                            {txn.type === 'credit' ? (
                              <ArrowDownRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ArrowUpRight className="h-4 w-4 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm text-slate-800">{txn.description}</p>
                            <p className="text-xs text-slate-500 text-nowrap">{new Date(txn.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-semibold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : ''}₹{Math.abs(txn.amount || 0).toLocaleString()}
                          </p>
                          <p className="text-xs text-slate-500 text-nowrap">Balance: ₹{(txn.balance_after || 0).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Razorpay Wallet Top-up Dialog */}
      <WalletTopup 
        open={topupOpen} 
        onOpenChange={setTopupOpen} 
        onSuccess={handleTopupSuccess}
      />
    </DashboardLayout>
  );
}
